import React, { useEffect } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { useLocation } from 'react-router-dom';
import Loader from 'react-loader';
import socketio from 'socket.io-client';

import 'react-widgets/styles.css';

import { ADMIN_OPERATOR_EVENT_TYPE_URL, WEBSOCKET_URL } from 'upstream-shared/urls';
import { UpstreamToken } from 'upstream-shared/models';
import idUtil from 'upstream-shared/idUtil';

import { ADMIN_OPERATOR_EVENT_TYPE_STORE_ID } from '../../storeIds';
import { leavePage, visitPage } from '../../actions/usageActions';
import { readAdminObjects } from '../../actions/adminObjectsActions';
import { readWellLookups } from '../../actions/wellActions';
  import { updateMaintenanceWindow, updateDataProcessingQueue, updateLoadStatus } from '../../upstreamApi/apiActions';
import { useAppDispatch, useAppSelector } from '../../hooks';
import AdminPage from './AdminPage';
import ContentPage from './ContentPage';
import MapContext from './MapContext';

import './App.css';
import PlatformUpdateBanner from './PlatformUpdateBanner';


const socket = socketio({ path: WEBSOCKET_URL });

const App = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const authUser: UpstreamToken | undefined = useAppSelector((state) => state.auth.authUser);
  const authError: string | undefined = useAppSelector((state) => state.auth.error);
  const isFetching: boolean = useAppSelector(
    (state) =>
      state.auth.isLoggingIn ||
      state.wellLookups.isFetching ||
      state.adminObjects[ADMIN_OPERATOR_EVENT_TYPE_STORE_ID]?.isFetching,
  );

  const googleMapsKey = 'AIzaSyC_WbwbDx7cTqdJxA_0b5uJM3a3oGfCJ2Q';
  const { isLoaded } = useLoadScript({ googleMapsApiKey: googleMapsKey });
  const isLoggedIn = !!authUser;

  useEffect(() => {
    dispatch(readWellLookups());
    dispatch(readAdminObjects(ADMIN_OPERATOR_EVENT_TYPE_URL, ADMIN_OPERATOR_EVENT_TYPE_STORE_ID));
  }, [dispatch]);

  useEffect(() => {
    socket.on('NextMaintenanceBegins', ({ nextMaintenanceBegins, loadUpdatedAt, loadStatus } = {}) => {
      dispatch(updateMaintenanceWindow(nextMaintenanceBegins ? new Date(nextMaintenanceBegins) : undefined));
      dispatch(updateLoadStatus({
        loadStartedAt: nextMaintenanceBegins ? new Date(nextMaintenanceBegins) : null,
        loadUpdatedAt: loadUpdatedAt ? new Date(loadUpdatedAt) : null,
        loadStatus: loadStatus ?? null,
      }));
    });
    socket.on('DataProcessingQueue', (dataProcessingQueue) => {
      dispatch(updateDataProcessingQueue(dataProcessingQueue));
    });
    return () => {
      socket.off('NextMaintenanceBegins');
      socket.off('DataProcessingQueue');
    };
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      const visitId = idUtil.generateBase32Id();
      const handleBeforeUnload = () => {
        if (navigator.sendBeacon && document.visibilityState === 'hidden') {
          navigator.sendBeacon(`/api/usage/${visitId}`);
        }
      };
      dispatch(visitPage(visitId, pathname));
      window.addEventListener('visibilitychange', handleBeforeUnload);
      return () => {
        window.removeEventListener('visibilitychange', handleBeforeUnload);
        dispatch(leavePage(visitId, pathname));
      };
    }
  }, [dispatch, pathname, isLoggedIn]);

  const PageWrapper = pathname.includes('/admin/') ? AdminPage : ContentPage;

  return (
    <Loader loaded={!isFetching}>
      <MapContext.Provider value={isLoaded}>
        <PageWrapper authUser={authUser} authError={authError} />
        <PlatformUpdateBanner />
      </MapContext.Provider>
    </Loader>
  );
};

export default App;
